import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addKnowledgeFact,
  bulkReviewKnowledge,
  type CommentDraftDto,
  commentOnPost,
  deleteKnowledgeFact,
  draftComment,
  type EngageConfigDto,
  getCommentDraft,
  getContact,
  getContactKnowledge,
  getContactTimeline,
  getEngageConfig,
  type KnowledgeFactDto,
  listContacts,
  type ManualLanguage,
  mergeContacts,
  patchContactLanguage,
  putAutoRespond,
  reactToPost,
  reassignIdentity,
  reviewKnowledgeFact,
  setContactKeepInTouch,
  undoMerge,
} from '../lib/api.js';
import {
  formatAbsolute,
  formatChannel,
  formatRelative,
  maskExternalIdForDisplay,
} from '../lib/format.js';
import type {
  CompanyDto,
  ContactActivityDto,
  ContactDetailDto,
  ContactIdentityDto,
  ContactListItemDto,
  ContactTimelineItem,
  LinkedinProfileDto,
} from '../types.js';
import { ChannelBadge } from './ChannelBadge.js';
import { ProfilePhoto } from './ProfilePhoto.js';
import { EmptyState } from './states/EmptyState.js';
import { ErrorState } from './states/ErrorState.js';
import { LoadingState } from './states/LoadingState.js';

const PAGE_LIMIT = 50;

/**
 * Cross-platform address book — split into a queue (the list, slot
 * 1) and a detail panel (the right pane). Both components live here
 * so the data-fetching wiring is co-located and easy to reason about.
 *
 * Why a split: the parent Layout owns the 3-pane operator console
 * grid; rendering a self-contained "AddressBook with internal split"
 * inside one slot would either waste columns or fight the grid. The
 * split lets contacts share the threads layout shape exactly.
 *
 * No bodies are fetched here; the address book is a "who do I know"
 * index, not a conversation view.
 */

interface QueueProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * Optional pre-set channel filter ("linkedin", "telegram", …). The
   * UI exposes its own channel toggle and uses this only as the
   * initial value. Kept as a prop so embedded usages can pin a
   * channel (e.g. a future "WhatsApp-only address book" view).
   */
  channel?: ChannelFilter;
}

/** Same shape as ThreadQueue's filter so the two surfaces stay in sync. */
type ChannelFilter = null | 'linkedin' | 'telegram' | 'whatsapp' | 'email';

const CHANNEL_OPTIONS: ReadonlyArray<{ value: ChannelFilter; label: string }> = [
  { value: null, label: 'All' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
];

export function AddressBookQueue({ selectedId, onSelect, channel: initialChannel }: QueueProps) {
  const [filter, setFilter] = useState('');
  const [channel, setChannel] = useState<ChannelFilter>(initialChannel ?? null);

  const listQuery = useInfiniteQuery({
    queryKey: ['contacts', { channel, q: filter.trim() || undefined }],
    queryFn: ({ pageParam, signal }) =>
      listContacts({
        limit: PAGE_LIMIT,
        before: pageParam,
        signal,
        ...(channel ? { channel } : {}),
        ...(filter.trim() ? { q: filter.trim() } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const items = useMemo<ContactListItemDto[]>(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  );

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="address-book-queue">
      <div className="flex flex-col gap-1.5 border-b border-zinc-800 px-3 py-2">
        <div
          className="flex gap-1 text-[10px]"
          role="tablist"
          aria-label="Channel filter"
          data-testid="contact-channel-filter"
        >
          {CHANNEL_OPTIONS.map((opt) => {
            const active = channel === opt.value;
            return (
              <button
                key={opt.value ?? 'all'}
                type="button"
                onClick={() => setChannel(opt.value)}
                data-testid={`contact-channel-${opt.value ?? 'all'}`}
                aria-selected={active}
                role="tab"
                className={`rounded px-1.5 py-0.5 ${
                  active
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'border border-zinc-800 text-zinc-400 hover:bg-zinc-800'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <input
          type="search"
          placeholder="Search name, handle, id…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter contacts"
          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-zinc-600 focus:outline-none"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {listQuery.isPending ? (
          <LoadingState label="Loading contacts" />
        ) : listQuery.isError ? (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            title="No contacts"
            hint={
              filter ? 'Clear the filter or try a different term.' : 'No address book entries yet.'
            }
          />
        ) : (
          <ul className="divide-y divide-zinc-900">
            {items.map((c) => (
              <ContactRow
                key={c.id}
                contact={c}
                selected={c.id === selectedId}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
        {listQuery.hasNextPage ? (
          <div className="border-t border-zinc-900 p-2">
            <button
              type="button"
              disabled={listQuery.isFetchingNextPage}
              onClick={() => void listQuery.fetchNextPage()}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              {listQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * batch-189 — the address book renders the detail as tabs (its hero owns the
 * tab bar + passes the active `tab`); the Classic UI passes no `tab` and gets
 * the full single-scroll layout it always had.
 */
export type ContactDetailTab = 'overview' | 'knowledge' | 'auto' | 'activity' | 'identities';

interface DetailProps {
  contactId: string | null;
  onSelectThread: (threadId: string) => void;
  /** batch-189 — when set, render ONLY this tab's sections (address book). */
  tab?: ContactDetailTab;
  /** batch-189 — skip the built-in header (the address book renders its hero). */
  hideHeader?: boolean;
}

export function ContactDetailPanel({ contactId, onSelectThread, tab, hideHeader }: DetailProps) {
  const query = useQuery({
    queryKey: ['contact', contactId],
    queryFn: ({ signal }) => getContact(contactId ?? '', { signal }),
    enabled: contactId !== null,
  });

  if (contactId === null) {
    return (
      <EmptyState
        title="Select a contact"
        hint="Pick a row from the address book to view identities and recent threads."
      />
    );
  }
  if (query.isPending) return <LoadingState label="Loading contact" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const c: ContactDetailDto = query.data;
  const facts = c.profile_facts ?? null;

  const header =
    hideHeader === true ? null : (
      // Aggregated header: photo + name + cross-channel profile facts.
      <div className="mb-4 flex items-start gap-3">
        <ProfilePhoto src={facts?.profile_image_url ?? null} label={c.display_name ?? 'Unknown'} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-100">
            {c.display_name ?? 'Unknown contact'}
          </div>
          {facts && (facts.role || facts.company || facts.location) ? (
            <div className="mt-0.5 text-[11px] text-zinc-300">
              {[facts.role, facts.company].filter(Boolean).join(' · ')}
              {facts.location ? (
                <span className="text-zinc-500">
                  {facts.role || facts.company ? ' — ' : ''}
                  {facts.location}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mt-0.5 text-[10px] text-zinc-500">
            {c.identities.length} {c.identities.length === 1 ? 'identity' : 'identities'} across{' '}
            {new Set(c.identities.map((i) => i.channel)).size} channels · {c.threads.length}{' '}
            {c.threads.length === 1 ? 'conversation' : 'conversations'}
          </div>
        </div>
      </div>
    );

  const identitiesSection = (
    <Section title="Identities">
      {c.identities.length === 0 ? (
        <div className="text-zinc-500">No identities on file.</div>
      ) : (
        <div className="space-y-3">
          {groupByChannel(c.identities).map(([ch, ids]) => (
            <div key={ch}>
              <div className="mb-1 flex items-center gap-2">
                <ChannelBadge channel={ch} size="xs" />
                <span className="text-zinc-200">{formatChannel(ch)}</span>
                <span className="ml-auto text-[10px] text-zinc-500">
                  {ids.length} {ids.length === 1 ? 'identity' : 'identities'}
                </span>
              </div>
              <ul className="space-y-2 pl-1">
                {ids.map((id) => (
                  <IdentityRow key={id.id} identity={id} currentContactId={c.id} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Section>
  );

  const timelineSection = (
    <Section title="Conversation timeline (all channels)">
      <ContactTimeline contactId={c.id} onSelectThread={onSelectThread} />
    </Section>
  );

  // batch-189 — address book: render ONLY the requested tab's sections (the hero
  // + tab bar live in the address-book wrapper). The sub-components are reused
  // verbatim, so behaviour matches the Classic single-scroll exactly.
  if (tab) {
    return (
      <div
        data-testid="contact-detail"
        className="flex h-full min-h-0 flex-col overflow-y-auto p-4 text-xs"
      >
        {tab === 'overview' && (
          <>
            <KeepInTouchControls contact={c} />
            <LinkedinProfile profile={c.linkedin_profile ?? null} />
            <CompanyIntel company={c.company ?? null} />
            <RecentActivity posts={c.recent_activity ?? []} />
          </>
        )}
        {tab === 'knowledge' && <KnowledgePanel contactId={c.id} onSelectThread={onSelectThread} />}
        {tab === 'auto' && (
          <>
            <AutoRespondConfig contact={c} />
            <LanguagePreference contact={c} />
          </>
        )}
        {tab === 'activity' && timelineSection}
        {tab === 'identities' && (
          <>
            {identitiesSection}
            <MergeControls contact={c} />
          </>
        )}
      </div>
    );
  }

  // Classic UI: full single-scroll layout (unchanged).
  return (
    <div
      data-testid="contact-detail"
      className="flex h-full min-h-0 flex-col overflow-y-auto p-4 text-xs"
    >
      {header}
      <MergeControls contact={c} />
      <LanguagePreference contact={c} />
      <AutoRespondConfig contact={c} />
      <LinkedinProfile profile={c.linkedin_profile ?? null} />
      <CompanyIntel company={c.company ?? null} />
      <KnowledgePanel contactId={c.id} onSelectThread={onSelectThread} />
      <RecentActivity posts={c.recent_activity ?? []} />
      {identitiesSection}
      {timelineSection}
    </div>
  );
}

/**
 * One identity row + a "Reassign…" affordance to move this channel identity
 * (and its conversations) to a different contact — the per-conversation link
 * flow. Reuses the shared ContactPicker.
 */
function IdentityRow({
  identity,
  currentContactId,
}: {
  identity: ContactIdentityDto;
  currentContactId: string;
}) {
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const reassign = useMutation({
    mutationFn: (targetContactId: string) => reassignIdentity(identity.id, targetContactId),
    onSuccess: () => {
      setPicking(false);
      void queryClient.invalidateQueries({ queryKey: ['contact', currentContactId] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  return (
    <li>
      {identity.handle ? (
        <div className="font-mono text-[11px] text-zinc-300">@{identity.handle}</div>
      ) : null}
      <div className="font-mono text-[10px] text-zinc-500 break-all">
        {maskExternalIdForDisplay(identity.channel, identity.external_id)}
      </div>
      {identity.metadata && Object.keys(identity.metadata).length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {Object.entries(identity.metadata).map(([k, v]) => (
            <li key={k} className="grid grid-cols-[7rem_1fr] gap-2">
              <span className="text-zinc-500">{k}</span>
              <span className="text-zinc-300 break-words">{String(v)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-0.5 flex items-center gap-2">
        <span className="text-[10px] text-zinc-600">added {formatAbsolute(identity.added_at)}</span>
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          data-testid="identity-reassign"
          className="text-[10px] text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline"
        >
          {picking ? 'Cancel' : 'Reassign…'}
        </button>
      </div>
      {picking ? (
        <div className="mt-1.5">
          {reassign.isError ? (
            <div className="mb-1 text-[10px] text-rose-400">Reassign failed.</div>
          ) : null}
          <ContactPicker
            excludeContactId={currentContactId}
            placeholder="Move this identity to…"
            disabled={reassign.isPending}
            onPick={(target) => reassign.mutate(target)}
          />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Search-and-pick a contact. Used by the merge + reassign flows. Excludes the
 * current contact and (via the list API) any already-merged-away contacts.
 */
export function ContactPicker({
  excludeContactId,
  placeholder,
  disabled,
  onPick,
}: {
  excludeContactId: string;
  placeholder: string;
  disabled?: boolean;
  onPick: (contactId: string) => void;
}) {
  const [q, setQ] = useState('');
  const results = useQuery({
    queryKey: ['contact-picker', q],
    queryFn: ({ signal }) => listContacts({ q: q.trim(), limit: 8, signal }),
    enabled: q.trim().length >= 2,
  });
  const items = (results.data?.items ?? []).filter((c) => c.id !== excludeContactId);
  return (
    <div>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={placeholder}
        className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-[11px] text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-zinc-600 disabled:opacity-50"
      />
      {q.trim().length >= 2 ? (
        <ul className="mt-1 max-h-40 overflow-y-auto rounded border border-zinc-800">
          {items.length === 0 ? (
            <li className="px-2 py-1 text-[10px] text-zinc-500">
              {results.isPending ? 'Searching…' : 'No matches.'}
            </li>
          ) : (
            items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(c.id)}
                  data-testid="contact-picker-result"
                  className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-zinc-800 disabled:opacity-50"
                >
                  <span className="min-w-0 flex-1 truncate text-[11px] text-zinc-100">
                    {c.display_name ?? 'Unknown contact'}
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {c.channels.map((ch) => (
                      <ChannelBadge key={ch} channel={ch} size="xs" />
                    ))}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Merge controls for a contact: fold a duplicate INTO this contact, and undo
 * any prior merges into it. All reversible (the engine logs every move).
 */
function MergeControls({ contact }: { contact: ContactDetailDto }) {
  const queryClient = useQueryClient();
  const [merging, setMerging] = useState(false);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
    void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    void queryClient.invalidateQueries({ queryKey: ['contact-timeline', contact.id] });
  };
  const merge = useMutation({
    mutationFn: (sourceContactId: string) => mergeContacts(sourceContactId, contact.id),
    onSuccess: () => {
      setMerging(false);
      invalidate();
    },
  });
  const undo = useMutation({
    mutationFn: (mergeId: string) => undoMerge(mergeId),
    onSuccess: invalidate,
  });
  const mergesIn = contact.merges_in ?? [];

  return (
    <Section title="Link / merge">
      <button
        type="button"
        onClick={() => setMerging((v) => !v)}
        data-testid="merge-toggle"
        className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-200 hover:bg-zinc-800"
      >
        {merging ? 'Cancel' : 'Merge a duplicate into this contact'}
      </button>
      {merging ? (
        <div className="mt-1.5">
          <ContactPicker
            excludeContactId={contact.id}
            placeholder="Find the duplicate contact…"
            disabled={merge.isPending}
            onPick={(source) => merge.mutate(source)}
          />
          {merge.isError ? (
            <div className="mt-1 text-[10px] text-rose-400">Merge failed — try again.</div>
          ) : null}
        </div>
      ) : null}
      {mergesIn.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {mergesIn.map((m) => (
            <li key={m.merge_id} className="flex items-center gap-2 text-[10px]">
              <span className="min-w-0 truncate text-zinc-400">
                Merged in{' '}
                <span className="text-zinc-200">{m.source_display_name ?? 'a contact'}</span>
              </span>
              <button
                type="button"
                onClick={() => undo.mutate(m.merge_id)}
                disabled={undo.isPending}
                data-testid="merge-undo"
                className="ml-auto shrink-0 text-zinc-400 underline-offset-2 hover:text-zinc-200 hover:underline disabled:opacity-50"
              >
                Undo
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {undo.isError ? (
        <div className="mt-1 text-[10px] text-rose-400" role="alert">
          Undo failed — try again.
        </div>
      ) : null}
    </Section>
  );
}

/** Unified cross-channel message timeline (newest-first, paginated). */
function ContactTimeline({
  contactId,
  onSelectThread,
}: {
  contactId: string;
  onSelectThread: (threadId: string) => void;
}) {
  const q = useInfiniteQuery({
    queryKey: ['contact-timeline', contactId],
    queryFn: ({ pageParam, signal }) =>
      getContactTimeline(contactId, { limit: 30, before: pageParam, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
  });

  if (q.isPending) return <LoadingState label="Loading timeline" />;
  if (q.isError) return <div className="text-[10px] text-rose-400">Failed to load timeline.</div>;
  const items = q.data.pages.flatMap((p) => p.items);
  if (items.length === 0) return <div className="text-zinc-500">No messages yet.</div>;

  return (
    <div className="space-y-1.5" data-testid="contact-timeline">
      {items.map((m) => (
        <TimelineRow key={`${m.item_type}:${m.id}`} item={m} onOpenThread={onSelectThread} />
      ))}
      {q.hasNextPage ? (
        <button
          type="button"
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
          className="w-full rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-400 hover:bg-zinc-800 disabled:opacity-50"
        >
          {q.isFetchingNextPage ? 'Loading…' : 'Load older'}
        </button>
      ) : null}
    </div>
  );
}

function TimelineRow({
  item,
  onOpenThread,
}: {
  item: ContactTimelineItem;
  onOpenThread: (threadId: string) => void;
}) {
  // batch-288 — a meeting row (no thread to open; distinct marker).
  if (item.item_type === 'meeting') {
    const detail = [
      item.is_online ? 'online' : null,
      item.location?.trim() || null,
      item.categories.length ? item.categories.join(', ') : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <div
        data-testid="timeline-item"
        data-meeting="true"
        className="flex w-full flex-col gap-0.5 rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-left"
      >
        <div className="flex items-center gap-1.5 text-[9px] text-zinc-500">
          <span aria-hidden>📅</span>
          <span>Meeting{detail ? ` · ${detail}` : ''}</span>
          <span className="ml-auto">{formatRelative(item.source_timestamp)}</span>
        </div>
        <div className="line-clamp-2 whitespace-pre-wrap break-words text-[11px] text-zinc-200">
          {item.subject ?? 'Meeting'}
        </div>
      </div>
    );
  }
  const outbound = item.direction === 'outbound';
  const text = item.body_text?.trim()
    ? item.body_text
    : (item.attachment_summary ??
      item.subject ??
      (item.has_body_html ? 'HTML message' : 'No text content'));
  return (
    <button
      type="button"
      onClick={() => onOpenThread(item.thread_id)}
      data-testid="timeline-item"
      className={`flex w-full flex-col gap-0.5 rounded border border-zinc-800 px-2 py-1.5 text-left hover:bg-zinc-800 ${
        outbound ? 'bg-sky-950/30' : 'bg-zinc-900'
      }`}
    >
      <div className="flex items-center gap-1.5 text-[9px] text-zinc-500">
        <ChannelBadge channel={item.channel} size="xs" />
        <span>{outbound ? 'You' : 'Them'}</span>
        <span className="ml-auto">{formatRelative(item.source_timestamp)}</span>
      </div>
      <div className="line-clamp-2 whitespace-pre-wrap break-words text-[11px] text-zinc-200">
        {text}
      </div>
    </button>
  );
}

function ContactRow({
  contact,
  selected,
  onSelect,
}: {
  contact: ContactListItemDto;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(contact.id)}
        aria-current={selected ? 'true' : undefined}
        data-testid="contact-row"
        className={`group flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
          selected ? 'bg-zinc-900' : 'hover:bg-zinc-900/60'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-zinc-100">
              {contact.display_name ?? <span className="italic text-zinc-500">Unknown</span>}
            </span>
            <span className="ml-auto shrink-0 text-[10px] text-zinc-500">
              {formatRelative(contact.last_interaction_at)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            {contact.channels.length === 0 ? (
              <span className="text-[10px] italic text-zinc-600">no channels</span>
            ) : (
              contact.channels.map((c) => <ChannelBadge key={c} channel={c} size="xs" />)
            )}
            <span className="ml-auto text-[10px] tabular-nums text-zinc-500">
              {contact.thread_count} {contact.thread_count === 1 ? 'thread' : 'threads'}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}

/**
 * Group an identity list by `channel` for the contact-detail view.
 *
 *   - Stable order: insertion order from the input, which the API
 *     already sorts by (channel ASC, created_at ASC). Map preserves
 *     insertion order in modern JS engines.
 *   - Returns an array of [channel, identities[]] tuples so the
 *     consumer can render with a stable key per group.
 */
/**
 * Compact language-preference row: pill showing the current
 * preference + source, plus a `<select>` for manual override. The
 * select fires PATCH /contacts/:id/language and invalidates the
 * cached contact-detail query so the pill re-renders with the new
 * value + source='manual'. "Auto" clears the manual choice and lets
 * the next inference run repopulate.
 */
export function LanguagePreference({ contact }: { contact: ContactDetailDto }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (language: ManualLanguage | null) => patchContactLanguage(contact.id, language),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
    },
  });

  const value =
    contact.language_source === 'manual' || contact.language_source === 'inferred'
      ? (contact.preferred_language ?? 'auto')
      : 'auto';

  return (
    <div
      data-testid="language-preference"
      className="mb-4 flex items-center gap-2 rounded border border-zinc-800 bg-zinc-900/40 px-2 py-1.5"
    >
      <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        Language
      </span>
      <span
        data-testid="language-pill"
        className="rounded bg-zinc-900 px-1.5 py-0.5 text-[10px] uppercase ring-1 ring-inset ring-zinc-800 text-zinc-200"
      >
        {contact.preferred_language ?? '—'}
      </span>
      <span className="text-[10px] text-zinc-500">
        {contact.language_source ?? 'unset'}
        {contact.language_source === 'inferred' &&
        contact.language_confidence !== null &&
        contact.language_confidence !== undefined
          ? ` · ${Math.round(contact.language_confidence * 100)}%`
          : null}
      </span>
      <select
        aria-label="Override language"
        data-testid="language-select"
        disabled={mutation.isPending}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'auto') mutation.mutate(null);
          else mutation.mutate(v as ManualLanguage);
        }}
        className="ml-auto rounded border border-zinc-700 bg-zinc-900 px-1 py-0.5 text-[11px] text-zinc-200 focus:border-zinc-500 focus:outline-none"
      >
        <option value="auto">Auto</option>
        <option value="en">English</option>
        <option value="ru">Russian</option>
        <option value="ar">Arabic</option>
      </select>
      {mutation.isError ? (
        <span className="text-[10px] text-rose-400" role="alert">
          Failed
        </span>
      ) : null}
    </div>
  );
}

/**
 * batch-64/70 — selective auto-respond (testing). Per-contact opt-in + per-user
 * prompt + a "Go live" toggle. LIVE across CHAT channels (LinkedIn, Telegram,
 * WhatsApp): when enabled + Go-live (and the master switch + guardrails pass),
 * the worker auto-sends the reply on this contact's threads. Without Go-live it's
 * shadow (the prompt still shapes the drafts, nothing sends). Email is not yet
 * an auto-respond channel.
 */
const AUTO_RESPOND_CHANNELS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email' },
];

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',');
}

/**
 * batch-85 — the contact knowledge base. Shows operator-confirmed facts (which
 * feed the AI's drafts), a pending review queue (Confirm / Reject), recent
 * auto-respond probes asked, and a manual-add box. Learned facts are written by
 * the worker only when KNOWLEDGE_LEARNING_ENABLED is on; until then this panel
 * is simply empty (or shows manually-added facts).
 */
function KnowledgePanel({
  contactId,
  onSelectThread,
}: {
  contactId: string;
  onSelectThread?: (threadId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const query = useQuery({
    queryKey: ['contact-knowledge', contactId],
    queryFn: () => getContactKnowledge(contactId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['contact-knowledge', contactId] });

  const review = useMutation({
    mutationFn: (v: { id: string; status: 'confirmed' | 'rejected' }) =>
      reviewKnowledgeFact(contactId, v.id, v.status),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteKnowledgeFact(contactId, id),
    onSuccess: invalidate,
  });
  const add = useMutation({
    mutationFn: (content: string) => addKnowledgeFact(contactId, { content }),
    onSuccess: () => {
      setDraft('');
      invalidate();
    },
  });
  // Confirm/reject ALL of this contact's pending facts in one call (the per-fact
  // review doesn't scale across a big backlog).
  const bulkReview = useMutation({
    mutationFn: (status: 'confirmed' | 'rejected') =>
      bulkReviewKnowledge({ status, contact_id: contactId }),
    onSuccess: invalidate,
  });

  const facts = query.data?.facts ?? [];
  const pending = facts.filter((f) => f.status === 'pending');
  const confirmed = facts.filter((f) => f.status === 'confirmed');
  const probes = query.data?.probes ?? [];

  const openThread = (threadId: string | null) => {
    if (threadId && onSelectThread) onSelectThread(threadId);
  };

  return (
    <div
      data-testid="knowledge-panel"
      className="mb-4 rounded border border-zinc-800 bg-zinc-900/40 px-2 py-2"
    >
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        Knowledge base
      </div>

      {review.isError || remove.isError || add.isError || bulkReview.isError ? (
        <div
          className="mb-1 text-[11px] text-rose-400"
          role="alert"
          data-testid="knowledge-action-error"
        >
          Action failed — try again.
        </div>
      ) : null}

      {query.isLoading ? <div className="text-[11px] text-zinc-500">Loading…</div> : null}

      {pending.length > 0 ? (
        <div className="mb-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-[10px] font-medium uppercase tracking-wider text-amber-300/80">
              Pending review ({pending.length})
            </span>
            {pending.length > 1 ? (
              <span className="flex gap-1.5">
                <button
                  type="button"
                  data-testid="knowledge-confirm-all"
                  disabled={review.isPending || bulkReview.isPending}
                  onClick={() => bulkReview.mutate('confirmed')}
                  className="rounded bg-emerald-900 px-2 py-0.5 text-[10px] font-medium text-emerald-100 ring-1 ring-inset ring-emerald-800 hover:bg-emerald-800 disabled:opacity-50"
                >
                  Confirm all
                </button>
                <button
                  type="button"
                  data-testid="knowledge-reject-all"
                  disabled={review.isPending || bulkReview.isPending}
                  onClick={() => bulkReview.mutate('rejected')}
                  className="rounded bg-zinc-800 px-2 py-0.5 text-[10px] font-medium text-zinc-300 ring-1 ring-inset ring-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                >
                  Reject all
                </button>
              </span>
            ) : null}
          </div>
          <ul className="space-y-1.5">
            {pending.map((f) => (
              <li
                key={f.id}
                data-testid="knowledge-pending"
                className="rounded border border-amber-900/50 bg-amber-950/20 px-2 py-1.5"
              >
                <KnowledgeFactText fact={f} onOpenThread={openThread} />
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="knowledge-confirm"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: f.id, status: 'confirmed' })}
                    className="rounded bg-emerald-900 px-2 py-0.5 text-[10px] font-medium text-emerald-100 ring-1 ring-inset ring-emerald-800 hover:bg-emerald-800 disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    data-testid="knowledge-reject"
                    disabled={review.isPending}
                    onClick={() => review.mutate({ id: f.id, status: 'rejected' })}
                    className="rounded bg-zinc-800 px-2 py-0.5 text-[10px] font-medium text-zinc-300 ring-1 ring-inset ring-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
        Confirmed ({confirmed.length})
      </div>
      {confirmed.length === 0 ? (
        <div className="text-[11px] text-zinc-600">
          Nothing confirmed yet. Confirmed facts shape the AI's replies to this contact.
        </div>
      ) : (
        <ul className="space-y-1">
          {confirmed.map((f) => (
            <li
              key={f.id}
              data-testid="knowledge-confirmed"
              className="group flex items-start gap-2 text-[12px] text-zinc-200"
            >
              <span className="mt-1 size-1 shrink-0 rounded-full bg-emerald-500" />
              <span className="flex-1">
                <KnowledgeFactText fact={f} onOpenThread={openThread} />
              </span>
              <button
                type="button"
                data-testid="knowledge-delete"
                disabled={remove.isPending}
                onClick={() => remove.mutate(f.id)}
                className="opacity-0 transition group-hover:opacity-100 text-[10px] text-zinc-500 hover:text-red-300"
                title="Delete this fact"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-2 flex items-center gap-1.5">
        <input
          type="text"
          data-testid="knowledge-add-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a fact you know about this contact…"
          className="flex-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[12px] text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-600 focus:outline-none"
        />
        <button
          type="button"
          data-testid="knowledge-add"
          disabled={draft.trim().length === 0 || add.isPending}
          onClick={() => add.mutate(draft.trim())}
          className="rounded-md bg-zinc-800 px-2.5 py-1 text-[11px] font-medium text-zinc-200 ring-1 ring-inset ring-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {probes.length > 0 ? (
        <div className="mt-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
            Questions the AI asked
          </div>
          <ul className="space-y-0.5">
            {probes.map((p) => (
              <li key={p.id} className="text-[11px] text-zinc-500">
                <span className="text-zinc-400">“{p.content}”</span>{' '}
                <span className="text-zinc-600">· {formatRelative(p.learned_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function KnowledgeFactText({
  fact,
  onOpenThread,
}: {
  fact: KnowledgeFactDto;
  onOpenThread: (threadId: string | null) => void;
}) {
  return (
    <span className="text-[12px] text-zinc-200">
      {fact.category ? (
        <span className="mr-1 rounded bg-zinc-800 px-1 py-px text-[9px] uppercase tracking-wide text-zinc-400">
          {fact.category}
        </span>
      ) : null}
      {fact.content} <KnowledgeProvenance fact={fact} onOpenThread={onOpenThread} />
    </span>
  );
}

/**
 * Wave-2 — every fact shows WHERE it came from (the ledger's point): a
 * conversation is clickable to the source thread; a meeting / AI-inference /
 * manual entry shows a plain provenance label (those have no routed target).
 * AI-inferred facts also surface the extraction confidence.
 */
function KnowledgeProvenance({
  fact,
  onOpenThread,
}: {
  fact: KnowledgeFactDto;
  onOpenThread: (threadId: string | null) => void;
}) {
  const label = 'ml-1 whitespace-nowrap text-[10px] text-zinc-500';
  if (fact.source_type === 'conversation' && fact.source_thread_id) {
    return (
      <button
        type="button"
        data-testid="knowledge-source"
        onClick={() => onOpenThread(fact.source_thread_id)}
        className="ml-1 text-[10px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
        title="Open the conversation this came from"
      >
        💬 source
      </button>
    );
  }
  if (fact.source_type === 'meeting') {
    return (
      <span className={label} data-testid="knowledge-source" title="Captured from a meeting">
        📅 from a meeting
      </span>
    );
  }
  if (fact.source_type === 'analysis') {
    const pct =
      typeof fact.confidence === 'number' ? ` · ${Math.round(fact.confidence * 100)}%` : '';
    return (
      <span
        className={label}
        data-testid="knowledge-source"
        title="Inferred by the AI from this contact’s messages"
      >
        ✨ AI{pct}
      </span>
    );
  }
  return (
    <span className={label} data-testid="knowledge-source" title="Added manually">
      ✎ added by you
    </span>
  );
}

// batch-K3b.2 — keep-in-touch enrolment: set a contact's relationship tier (which
// drives cadence), an optional per-contact cadence override, a pause toggle, and a
// birthday. NULL tier = not enrolled. Writes via setContactKeepInTouch; the engine
// then surfaces this contact in the "Keep in touch" hub when due.
const KIT_TIERS = [
  { value: '', label: 'Not enrolled' },
  { value: 'inner', label: 'Inner circle' },
  { value: 'active', label: 'Active / important' },
  { value: 'wider', label: 'Wider network' },
  { value: 'personal', label: 'Personal' },
] as const;

function KeepInTouchControls({ contact }: { contact: ContactDetailDto }) {
  const queryClient = useQueryClient();
  const initialTier = contact.keep_in_touch_tier ?? '';
  const initialInterval = contact.keep_in_touch_interval_days ?? null;
  const initialPaused = contact.keep_in_touch_paused ?? false;
  const initialBday = (contact.important_dates ?? []).find((d) => d.kind === 'birthday');
  const [tier, setTier] = useState<string>(initialTier);
  const [interval, setIntervalDays] = useState<string>(
    initialInterval ? String(initialInterval) : '',
  );
  const [paused, setPaused] = useState(initialPaused);
  const [bMonth, setBMonth] = useState<string>(initialBday ? String(initialBday.month) : '');
  const [bDay, setBDay] = useState<string>(initialBday ? String(initialBday.day) : '');
  const [bYear, setBYear] = useState<string>(initialBday?.year ? String(initialBday.year) : '');
  const [saved, setSaved] = useState(false);

  const initialBdayKey = initialBday
    ? `${initialBday.month}-${initialBday.day}-${initialBday.year ?? ''}`
    : '-';
  const dirty =
    tier !== initialTier ||
    interval !== (initialInterval ? String(initialInterval) : '') ||
    paused !== initialPaused ||
    `${bMonth}-${bDay}-${bYear}` !== initialBdayKey;

  const touch = () => setSaved(false);

  const save = useMutation({
    mutationFn: () => {
      const dates: NonNullable<ContactDetailDto['important_dates']> = [];
      const m = Number.parseInt(bMonth, 10);
      const d = Number.parseInt(bDay, 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        const y = Number.parseInt(bYear, 10);
        dates.push({
          kind: 'birthday',
          month: m,
          day: d,
          ...(Number.isFinite(y) && y >= 1900 && y <= 2100 ? { year: y } : {}),
        });
      }
      const n = Number.parseInt(interval, 10);
      return setContactKeepInTouch(contact.id, {
        tier: tier || null,
        interval_days: Number.isFinite(n) && n > 0 ? n : null,
        paused,
        important_dates: dates,
      });
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
    },
  });

  return (
    <div
      data-testid="keep-in-touch-controls"
      className="mb-4 rounded border border-zinc-800 bg-zinc-900/40 px-2 py-2"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
          Keep in touch
        </span>
        {tier && !paused ? (
          <span className="rounded bg-emerald-950 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-900">
            Enrolled
          </span>
        ) : null}
      </div>
      <p className="mb-2 text-[10px] leading-snug text-zinc-500">
        Enrol this contact so Comms surfaces periodic check-ins in the “Keep in touch” hub. Pick a{' '}
        <span className="text-zinc-400">tier</span> and the engine proposes a touch when they're due
        and AI-drafts it in your voice for you to review &amp; send. (Birthday greetings to lower
        tiers may auto-send if you've armed that in Setup.) Leave the tier on “Not enrolled” to opt
        this contact out.
      </p>
      <label className="flex items-center justify-between gap-2 text-[11px] text-zinc-200">
        <span title="How often to reach out. Inner ≈ every few weeks; Active ≈ ~monthly; Wider ≈ a few times a year; Personal ≈ by closeness.">
          Tier
        </span>
        <select
          data-testid="keep-in-touch-tier"
          value={tier}
          onChange={(e) => {
            setTier(e.target.value);
            touch();
          }}
          className="rounded border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-100"
        >
          {KIT_TIERS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-zinc-200">
        <span title="Optional. Override the tier's default frequency for just this contact. Leave blank to use the tier default.">
          Cadence override (days)
        </span>
        <input
          type="number"
          min={1}
          placeholder="tier default"
          value={interval}
          onChange={(e) => {
            setIntervalDays(e.target.value);
            touch();
          }}
          className="w-24 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-100"
        />
      </label>
      <p className="mt-1 mb-1.5 text-[10px] leading-snug text-zinc-500">
        How many days between proactive touches to this contact. Leave blank to use the tier's
        default (Inner ~21d · Active ~45d · Wider ~120d · Personal ~60d). Set a number to make Comms
        reach out more or less often than the tier — e.g. <span className="text-zinc-400">30</span>{' '}
        = about monthly.
      </p>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-zinc-200">
        <span>Birthday (MM / DD / YYYY)</span>
        <span className="flex items-center gap-1">
          <input
            type="number"
            min={1}
            max={12}
            placeholder="MM"
            value={bMonth}
            onChange={(e) => {
              setBMonth(e.target.value);
              touch();
            }}
            className="w-12 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-100"
          />
          <span className="text-zinc-600">/</span>
          <input
            type="number"
            min={1}
            max={31}
            placeholder="DD"
            value={bDay}
            onChange={(e) => {
              setBDay(e.target.value);
              touch();
            }}
            className="w-12 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-100"
          />
          <span className="text-zinc-600">/</span>
          <input
            type="number"
            min={1900}
            max={2100}
            placeholder="YYYY"
            title="Optional — the birth year. Leave blank if you only know the month and day."
            value={bYear}
            onChange={(e) => {
              setBYear(e.target.value);
              touch();
            }}
            className="w-16 rounded border border-zinc-700 bg-zinc-950 px-1.5 py-1 text-[11px] text-zinc-100"
          />
        </span>
      </div>
      <label className="mt-1.5 flex items-center gap-2 text-[11px] text-zinc-200">
        <input
          type="checkbox"
          checked={paused}
          onChange={(e) => {
            setPaused(e.target.checked);
            touch();
          }}
        />
        Pause (keep enrolled, but don't surface)
      </label>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="rounded border border-zinc-700 px-2 py-1 text-[11px] text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && !dirty ? <span className="text-[11px] text-emerald-400">Saved ✓</span> : null}
        {save.isError ? <span className="text-[11px] text-red-300">Save failed.</span> : null}
      </div>
    </div>
  );
}

function AutoRespondConfig({ contact }: { contact: ContactDetailDto }) {
  const queryClient = useQueryClient();
  const initialChannels = contact.auto_respond_channels ?? [];
  const [enabled, setEnabled] = useState(contact.auto_respond_enabled ?? false);
  const [prompt, setPrompt] = useState(contact.auto_respond_prompt ?? '');
  const [channels, setChannels] = useState<string[]>(initialChannels);
  const [saved, setSaved] = useState(false);

  const dirty =
    enabled !== (contact.auto_respond_enabled ?? false) ||
    prompt !== (contact.auto_respond_prompt ?? '') ||
    !sameSet(channels, initialChannels);

  const toggleChannel = (key: string, on: boolean) => {
    setChannels((cur) => (on ? [...new Set([...cur, key])] : cur.filter((c) => c !== key)));
    setSaved(false);
  };

  const save = useMutation({
    mutationFn: () =>
      putAutoRespond(contact.id, {
        enabled,
        prompt: prompt.trim() || null,
        channels: enabled ? channels : [],
      }),
    onSuccess: (res) => {
      setEnabled(res.auto_respond_enabled);
      setPrompt(res.auto_respond_prompt ?? '');
      setChannels(res.auto_respond_channels ?? []);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
    },
  });

  const liveCount = enabled ? channels.length : 0;

  return (
    <div
      data-testid="auto-respond-config"
      className="mb-4 rounded border border-zinc-800 bg-zinc-900/40 px-2 py-2"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
          Auto-respond (testing)
        </span>
        <span className="rounded bg-amber-950 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-900">
          {liveCount > 0 ? `Auto · live (${liveCount})` : 'Auto · shadow'}
        </span>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-zinc-200">
        <input
          type="checkbox"
          data-testid="auto-respond-enabled"
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
            setSaved(false);
          }}
        />
        Auto-respond to this contact
      </label>
      <textarea
        data-testid="auto-respond-prompt"
        rows={3}
        value={prompt}
        disabled={!enabled || save.isPending}
        onChange={(e) => {
          setPrompt(e.target.value);
          setSaved(false);
        }}
        placeholder="How should the AI reply to this person? (their per-user prompt)"
        className="mt-1.5 w-full resize-y rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-600 focus:outline-none disabled:opacity-50"
      />
      <div className="mt-1.5">
        <div className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">
          Go live on (send real replies):
        </div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {AUTO_RESPOND_CHANNELS.map((ch) => (
            <label key={ch.key} className="flex items-center gap-1.5 text-[11px] text-zinc-300">
              <input
                type="checkbox"
                data-testid={`auto-respond-channel-${ch.key}`}
                checked={channels.includes(ch.key)}
                disabled={!enabled || save.isPending}
                onChange={(e) => toggleChannel(ch.key, e.target.checked)}
              />
              {ch.label}
            </label>
          ))}
        </div>
        <div className="mt-1 text-[10px] text-amber-300/80">
          Each selected channel auto-sends only if it's also globally enabled (and, for email, the
          email switch is on). No channels selected = shadow (nothing sends).
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          data-testid="auto-respond-save"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-purple-900 px-3 py-1 text-[11px] font-medium text-purple-100 ring-1 ring-inset ring-purple-800 hover:bg-purple-800 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && !dirty ? <span className="text-[10px] text-emerald-300">Saved.</span> : null}
        {save.isError ? <span className="text-[10px] text-red-300">Save failed.</span> : null}
      </div>
      <p className="mt-1.5 text-[10px] leading-snug text-zinc-500">
        The prompt shapes drafts for this contact now. Auto-send happens only on the channels you
        select above (each still gated by the global switches).
      </p>
    </div>
  );
}

function groupByChannel<T extends { channel: string }>(items: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const it of items) {
    const list = groups.get(it.channel) ?? [];
    list.push(it);
    groups.set(it.channel, list);
  }
  return Array.from(groups.entries());
}

/**
 * batch-118 (Phase A2) — employer firmographics (cached company snapshot).
 * Read-only; hidden when no company is cached for the contact.
 */
function CompanyIntel({ company }: { company: CompanyDto | null }) {
  if (!company?.name) return null;
  return (
    <Section title="Company">
      <div className="space-y-1 text-[12px] text-zinc-300">
        <div className="font-medium text-zinc-100">
          {company.profile_url ? (
            <a
              href={company.profile_url}
              target="_blank"
              rel="noreferrer"
              className="hover:text-zinc-300"
            >
              {company.name}
            </a>
          ) : (
            company.name
          )}
        </div>
        {company.tagline ? <div className="text-zinc-400">{company.tagline}</div> : null}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-zinc-500">
          {company.organization_type ? (
            <span>{company.organization_type.toLowerCase().replace(/_/g, ' ')}</span>
          ) : null}
          {company.location ? <span>{company.location}</span> : null}
          {typeof company.followers_count === 'number' ? (
            <span>{company.followers_count.toLocaleString()} followers</span>
          ) : null}
          {company.website ? (
            <a
              href={company.website}
              target="_blank"
              rel="noreferrer"
              className="hover:text-zinc-300"
            >
              website ↗
            </a>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

/**
 * batch-118 (Phase A1/A3) — deep LinkedIn profile: about, skills, experience,
 * education + network signals (1st-degree, mutual connections, follower count).
 * Read-only; from the latest profile snapshot. Hidden when empty.
 */
function LinkedinProfile({ profile }: { profile: LinkedinProfileDto | null }) {
  if (!profile) return null;
  const {
    summary,
    skills = [],
    experience = [],
    education = [],
    followerCount,
    connectionsCount,
    networkDistance,
    isConnection,
    sharedConnectionsCount,
  } = profile;
  const hasAny =
    summary ||
    skills.length ||
    experience.length ||
    education.length ||
    typeof followerCount === 'number' ||
    typeof sharedConnectionsCount === 'number' ||
    networkDistance;
  if (!hasAny) return null;
  const degree = networkDistance
    ? networkDistance
        .replace('_DEGREE', '')
        .replace('FIRST', '1st')
        .replace('SECOND', '2nd')
        .replace('THIRD', '3rd')
        .toLowerCase()
    : null;
  return (
    <Section title="LinkedIn profile">
      <div className="space-y-2 text-[12px] text-zinc-300">
        {(degree || isConnection || typeof sharedConnectionsCount === 'number') && (
          <div className="flex flex-wrap items-center gap-1.5">
            {degree ? (
              <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-200">
                {degree} degree
              </span>
            ) : null}
            {typeof sharedConnectionsCount === 'number' ? (
              <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-200">
                {sharedConnectionsCount} mutual
              </span>
            ) : null}
            {typeof followerCount === 'number' ? (
              <span className="text-[10px] text-zinc-500">
                {followerCount.toLocaleString()} followers
              </span>
            ) : null}
            {typeof connectionsCount === 'number' ? (
              <span className="text-[10px] text-zinc-500">
                {connectionsCount.toLocaleString()} connections
              </span>
            ) : null}
          </div>
        )}
        {summary ? (
          <div className="whitespace-pre-wrap text-zinc-300">
            {summary.length > 320 ? `${summary.slice(0, 320)}…` : summary}
          </div>
        ) : null}
        {experience.length > 0 ? (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
              Experience
            </div>
            {experience.slice(0, 4).map((e) => (
              <div
                key={`${e.company ?? ''}|${e.title ?? ''}|${e.start ?? ''}`}
                className="text-zinc-300"
              >
                {[e.title, e.company].filter(Boolean).join(' · ')}
                {e.start ? (
                  <span className="text-zinc-500">
                    {' '}
                    ({e.start}
                    {e.end === null ? '–present' : e.end ? `–${e.end}` : ''})
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
        {education.length > 0 ? (
          <div>
            <div className="mb-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
              Education
            </div>
            {education.map((e) => (
              <div key={`${e.school ?? ''}|${e.degree ?? ''}`} className="text-zinc-300">
                {[e.school, e.degree].filter(Boolean).join(' · ')}
              </div>
            ))}
          </div>
        ) : null}
        {skills.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {skills.slice(0, 12).map((s) => (
              <span
                key={s.name}
                className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-300"
              >
                {s.name}
                {typeof s.endorsements === 'number' && s.endorsements > 0 ? (
                  <span className="text-zinc-500"> {s.endorsements}</span>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/**
 * batch-117 (Phase A4) — recent LinkedIn activity (cached posts). Read-only;
 * surfaces what the contact is posting about (conversation context). Hidden when
 * there's no cached activity (populated by the gated fetch-linkedin-activity sweep).
 */
function RecentActivity({ posts }: { posts: ContactActivityDto[] }) {
  // batch-120 — engagement controls (react/comment) appear ONLY when armed
  // (LINKEDIN_ENGAGE_* on). Default OFF ⇒ this is just a read-only feed.
  const engage = useQuery<EngageConfigDto>({
    queryKey: ['engage-config'],
    queryFn: ({ signal }) => getEngageConfig({ signal }),
    staleTime: 60_000,
  });
  if (posts.length === 0) return null;
  return (
    <Section title="Recent LinkedIn activity">
      <div className="space-y-2">
        {posts.map((p) => (
          <LinkedinPostRow key={p.id} post={p} engage={engage.data ?? null} />
        ))}
      </div>
    </Section>
  );
}

function LinkedinPostRow({
  post: p,
  engage,
}: {
  post: ContactActivityDto;
  engage: EngageConfigDto | null;
}) {
  const [comment, setComment] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const social = p.social_id ?? null;
  const react = useMutation({
    mutationFn: () => reactToPost(social ?? '', 'like'),
    onSuccess: () => {
      setDone('Liked');
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'failed'),
  });
  const comment_ = useMutation({
    mutationFn: () => commentOnPost(social ?? '', comment.trim()),
    onSuccess: () => {
      setDone('Comment posted');
      setComment('');
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'failed'),
  });
  // batch-122 (B3) — AI draft a comment via the worker, then poll for it.
  const [drafting, setDrafting] = useState(false);
  const draftSince = useRef(0);
  const draftQ = useQuery({
    queryKey: ['comment-draft', social],
    queryFn: ({ signal }) => getCommentDraft(social ?? '', { signal }),
    enabled: drafting && Boolean(social),
    refetchInterval: drafting ? 2500 : false,
  });
  useEffect(() => {
    if (!drafting) return;
    const d: CommentDraftDto | null | undefined = draftQ.data?.draft;
    if (!d?.created_at || new Date(d.created_at).getTime() < draftSince.current) return;
    if (d.status === 'ready' && d.text) {
      setComment(d.text);
      setDone('Draft ready — review and send');
    } else {
      setErr(d.reason ?? 'no draft generated');
    }
    setDrafting(false);
  }, [draftQ.data, drafting]);
  const startDraft = useMutation({
    mutationFn: () => draftComment(social ?? ''),
    onSuccess: () => {
      draftSince.current = Date.now();
      setDrafting(true);
      setErr(null);
      setDone(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'failed'),
  });
  const fmtDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  };
  const canReact = engage?.react_enabled && social;
  const canComment = engage?.comment_enabled && social;
  return (
    <div className="rounded border border-zinc-800 bg-zinc-900/40 px-2 py-1.5">
      <div className="whitespace-pre-wrap text-[12px] text-zinc-200">
        {p.text ? (p.text.length > 280 ? `${p.text.slice(0, 280)}…` : p.text) : '(no text)'}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-500">
        {p.is_repost ? <span>repost</span> : null}
        {p.posted_at ? <span>{fmtDate(p.posted_at)}</span> : null}
        {typeof p.reaction_count === 'number' ? <span>{p.reaction_count} reactions</span> : null}
        {typeof p.comment_count === 'number' ? <span>{p.comment_count} comments</span> : null}
        {p.share_url ? (
          <a
            href={p.share_url}
            target="_blank"
            rel="noreferrer"
            className="text-zinc-400 hover:text-zinc-200"
          >
            open ↗
          </a>
        ) : null}
      </div>
      {canReact || canComment ? (
        <div className="mt-1.5 flex flex-col gap-1 border-t border-zinc-800 pt-1.5">
          {canReact ? (
            <button
              type="button"
              disabled={react.isPending}
              onClick={() => react.mutate()}
              className="self-start rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              👍 Like
            </button>
          ) : null}
          {canComment ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Comment (you approve each one)…"
                  className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-100 placeholder:text-zinc-600"
                />
                <button
                  type="button"
                  disabled={comment_.isPending || comment.trim().length === 0}
                  onClick={() => comment_.mutate()}
                  className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
                >
                  Send
                </button>
              </div>
              <button
                type="button"
                disabled={startDraft.isPending || drafting}
                onClick={() => startDraft.mutate()}
                className="self-start rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-400 hover:bg-zinc-800 disabled:opacity-50"
              >
                {drafting ? 'Drafting…' : '✨ AI draft'}
              </button>
            </div>
          ) : null}
          {done ? <span className="text-[10px] text-emerald-300">{done}</span> : null}
          {err ? <span className="text-[10px] text-rose-400">{err}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        {title}
      </div>
      {children}
    </div>
  );
}
